<template>
  <div>
    <!-- Search Address Book (If Logged In) -->
    <template v-if="customer && customer.id">
      <BookAddressSearch @selected="selectAddressBookEntry" />
      <div class="flex items-center my-8">
        <div class="flex-grow h-px bg-gray-200"></div>
        <div class="px-4 text-xs leading-none text-gray-500 uppercase">OR Enter Address Manually</div>
        <div class="flex-grow h-px bg-gray-200"></div>
      </div>
    </template>

    <!-- Contact -->
    <fieldset class="form-group">
      <label class="flex justify-between form-label">
        Contact
      </label>
      <div class="relative border border-gray-300 divide-y divide-gray-300 rounded shadow-sm">
        <div class="relative focus-within:z-50">
          <input name="name" type="text" class="border-transparent rounded-none rounded-t form-input" placeholder="Company Name (If Applicable)" v-model="address.name">
        </div>
        <div class="relative focus-within:z-50">
          <input name="contact_name" type="text" class="border-transparent rounded-none form-input" placeholder="Contact Person's Name" v-model="address.contact_name">
        </div>
        <div class="relative focus-within:z-50">
          <input type="tel" name="contact_phone" class="border-transparent rounded-none form-input" placeholder="Contact Person's Phone Number" v-model="address.contact_phone">
        </div>
        <div class="relative focus-within:z-50">
          <input type="email" name="contact_email" class="border-transparent rounded-none rounded-b form-input" autocomplete="disabled" placeholder="Contact Person's Email (Optional)" v-model="address.contact_email">
        </div>
      </div>
      <validations :errors="errors" field="contact" />
    </fieldset>

    <!-- Address -->
    <fieldset class="form-group">
      <label for="address" class="form-label">Address</label>
      <div class="relative border border-gray-300 divide-y divide-gray-300 rounded shadow-sm">
        <div class="relative focus-within:z-50">
          <AddressAutocomplete
            :id="`autocomplete-${address.id}`"
            name="address"
            ref="address"
            types=""
            input-class="border-transparent rounded-b-none form-input"
            placeholder="Street Address"
            @placechanged="getAddressData"
            @inputChange="changeAddressData"
            :initial-value="address.address_1" />
        </div>
        <div class="relative focus-within:z-50">
          <input type="text" placeholder="Apartment/Complex/Shop No." class="border-transparent rounded-none form-input" autocomplete="disabled" v-model="address.address_2">
        </div>
        <div class="grid grid-cols-6 divide-x divide-gray-300">
          <div class="relative col-span-4 focus-within:z-50">
            <input type="text" placeholder="City" class="col-span-4 border-transparent rounded-none form-input" autocomplete="disabled" v-model="address.city">
          </div>
          <div class="relative col-span-2 focus-within:z-50">
            <input type="text" placeholder="Zip/Postal Code" class="col-span-2 border-transparent rounded-none form-input" autocomplete="disabled" v-model="address.zip">
          </div>
        </div>
        <div class="relative focus-within:z-50">
          <country-select placeholder="Country or Region" v-model="address.country" class="border-transparent rounded-t-none form-select" />
        </div>
      </div>
      <validations :errors="errors" field="address" />
    </fieldset>

    <!-- Notes -->
    <fieldset class="form-group">
      <label for="notes" class="form-label">
        Notes 
        <div class="ml-1 text-xs text-gray-500">Optional</div>
      </label>
      <textarea rows="2" name="notes" class="form-textarea" autocomplete="disabled" v-model="address.notes" maxlength="255"></textarea>
      <validations :errors="errors" field="notes" />
    </fieldset>

    <!-- Reference -->
    <fieldset class="form-group">
      <label for="reference" class="form-label">
        Reference
        <div class="ml-1 text-xs text-gray-500">Optional</div>
      </label>
      <div class="mb-2 -mt-1 text-sm text-gray-500">
        Enter anything you'd like here for your own reference.
      </div>
      <input name="reference" type="text" class="form-input" autocomplete="disabled" v-model="address.reference">
      <validations :errors="errors" field="reference" />
    </fieldset>

    <!-- Products -->
    <div v-if="productsAvailable" class="mt-8">
      <div class="flex items-end justify-between pb-4">
        <label class="form-label">
          What are we {{ addressType === 'origins' ? 'collecting from' : 'delivering to' }} this address?
        </label>
      </div>
      <div class="relative overflow-auto bg-white border divide-y rounded shadow-sm max-h-72">
        <div v-for="product in options.products" :key="product.id">
          <BookProductItem
              :address="address"
              :product="product"
              @increase="increaseQuantity"
              @decrease="decreaseQuantity" />
        </div>
      </div>
      <validations :errors="errors" field="products" />
    </div>

    <!-- Addons -->
    <div v-if="addonsAvailable" class="mt-8">
      <div class="pb-3">
        <label class="mb-0 form-label">
          Additional Requests
        </label>
        <div class="text-sm text-gray-500">
          Check all that apply:
        </div>
      </div>
      <div class="grid overflow-hidden bg-white border divide-y rounded rounded-b shadow-sm">
        <fieldset v-for="addon in options.addons" :key="addon.id">
          <label class="flex items-start p-4 hover:bg-gray-50">
            <input type="checkbox" class="mr-3 transform translate-y-1 form-checkbox" :value="addon.id" :checked="addonChecked(addon.id)" @change="toggleAddon">
            <div class="flex-grow">
              <div class="flex justify-between text-sm font-medium">
                {{ addon.name }}
                <div class="flex items-center px-2 ml-2 text-xs font-normal leading-none text-gray-500 bg-gray-100 rounded-full" v-if="addon.price">
                  +<currency :value="displayPrice(addon.price)" :currency-code="portal.currency" :subunits-value="true" />
                  {{ portal.display_prices_excluding_tax ? `(excl. ${portal.tax_name})` : `(incl. ${portal.tax_name})` }}
                </div>
              </div>
              <div class="text-sm text-gray-500 leading-tiht">
                {{ addon.description }}
              </div>
            </div>
          </label>
        </fieldset>
      </div>
      <validations :errors="errors" field="addons" />
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import parsePhoneNumber from 'libphonenumber-js'

import AddressAutocomplete from '@/components/AddressAutocomplete'
import BookAddressSearch from './BookAddressSearch'
import BookProductItem from './BookProductItem'

export default {
  name: 'BookAddressForm',

  components: {
    AddressAutocomplete,
    BookAddressSearch,
    BookProductItem
  },

  props: {
    addressIndex: {
      type: Number
    },

    addressType: {
      type: String,
      required: true
    }
  },

  created () {
    if (this.addressIndex > -1) {
      this.address = JSON.parse(JSON.stringify(this.form[this.addressType][this.addressIndex]))
    } else {
      this.address = JSON.parse(JSON.stringify(this.addressTemplate))
    }
  },

  computed: {
    ...mapState(['portal']),
    ...mapState('booking', ['form', 'options', 'addressTemplate']),
    ...mapState('customer', ['customer']),

    productsAvailable () {
      if (this.options.products.length < 1) {
        return false
      }

      if (this.addressType === 'origins') {
        return this.options.allow_multiple_origins
      } else {
        return this.options.allow_multiple_destinations
      }
    },

    addonsAvailable () {
      if (this.options.addons.length < 1) {
        return false
      }

      if (this.addressType === 'origins') {
        return this.options.allow_multiple_origins
      } else {
        return this.options.allow_multiple_destinations
      }
    },

    slidesPerView () {
      // TODO: use $refs.slider width for this with Observer
      if (window.innerWidth > 500) {
        return 2.5
      } else {
        return 1.5
      }
    }
  },

  data () {
    return {
      address: {},
      errors: []
    }
  },

  methods: {
    ...mapMutations('booking', [
      'addAddress',
      'updateAddress'
    ]),

    reset () {
      this.address = JSON.parse(JSON.stringify(this.addressTemplate))
      this.$refs.address.$refs.address.$el.value = ''
    },

    changeAddressData (event) {
      this.address.address_1 = event.newVal
    },

    getAddressData ({ addressData, placeResultData }) {
      for (let component of placeResultData.address_components) {
        if (component.types.includes('country')) {
          this.address.country = component.short_name
        }
      }
      this.address.formatted_address = placeResultData.formatted_address
      this.address.city = addressData.locality
      this.address.zip = addressData.postal_code
      this.address.latitude = addressData.latitude
      this.address.longitude = addressData.longitude
      this.address.address_1 = placeResultData.formatted_address.split(',')[0] + ',' + placeResultData.formatted_address.split(',')[1]
      this.$refs.address.$refs.address.$el.value = this.address.address_1
    },

    selectAddressBookEntry (address) {
      this.address.name = address.name
      this.address.contact_name = address.contact_name
      this.address.contact_phone = address.contact_phone
      this.address.contact_email = address.contact_email

      this.address.formatted_address = address.formatted_address
      this.address.address_1 = address.address_1
      this.address.address_2 = address.address_2
      this.address.city = address.city
      this.address.province = address.province
      this.address.zip = address.zip
      this.address.country = address.country
      this.address.latitude = address.latitude
      this.address.longitude = address.longitude
      this.address.notes = ''
      this.address.reference = ''

      this.$nextTick(() => {
        if (this.address.address_1) {
          this.$refs.address.$refs.address.$el.value = this.address.address_1
        }
      })
    },

    increaseQuantity (item) {
      const productIndex = this.address.products.findIndex((product) => product.id === item.id)
      
      if (productIndex < 0) {
        this.address.products.push({ ...item, quantity: 1 })
      } else {
        this.address.products[productIndex].quantity += 1
      }
    },

    decreaseQuantity (item) {
      const productIndex = this.address.products.findIndex((product) => product.id === item.id)
      const productQuantity = this.address.products[productIndex].quantity
      
      if (productIndex < 0 || !productQuantity) {
        return
      } else if (productQuantity === 1) {
        this.$delete(this.address.products, productIndex)
      } else {
        this.address.products[productIndex].quantity -= 1
      }
    },

    addonChecked (id) {
      const addonExists = this.address.addons.find(addon => addon.id === id)
      return !!addonExists
    },

    toggleAddon (event) {
      const addonId = Number(event.target.value)
      const addonIndex = this.address.addons.findIndex(addon => addon.id === addonId)
      const addon = this.options.addons.find(addon => addon.id === addonId)

      if (addonIndex > -1) {
        this.$delete(this.address.addons, addonIndex)
      } else {
        this.address.addons.push(addon)
      }
    },

    displayPrice (value) {
      let price = value
      
      if (this.portal.display_prices_excluding_tax) {
        price = price / (1 + this.portal.tax_rate / 100)
      }

      if (this.portal.round_display_prices) {
        price = Math.round(price/100)*100
      }

      return price
    },

    submit () {
      let array = []
      
      if (!this.address.contact_name) {
        array.push({ 
          field: 'contact', 
          rule: 'contact_name_required', 
          message: `Contact person's name is required`
        })
      } 

      if (!this.address.contact_phone) {
        array.push({ 
          field: 'contact', 
          rule: 'contact_phone_required', 
          message: `Contact person's phone number is required` 
        })
      } else {
        const phoneNumber = parsePhoneNumber(this.address.contact_phone, this.address.country)
        if (!phoneNumber || !phoneNumber.isValid()) {
          array.push({ 
            field: 'contact', 
            rule: 'contact_phone_invalid', 
            message: `Contact person's phone number is not a valid phone number. Only enter one number. If you need to add another, you can put it in the notes section.` 
          })
        }
      }
      
      if (!this.address.address_1) {
        array.push({ 
          field: 'address', 
          rule: 'street_required', 
          message: 'Street Address is required' 
        })
      } 
      
      if (!this.address.city) {
        array.push({ 
          field: 'address', 
          rule: 'city_required', 
          message: 'City is required' 
        })
      } 
      
      if (!this.address.zip) {
        array.push({ 
          field: 'address', 
          rule: 'zip_required', 
          message: 'Zip is required' 
        })
      } 
      
      if (!this.address.country) {
        array.push({ 
          field: 'address', 
          rule: 'country_required', 
          message: 'Country is required' 
        })
      } 
      
      if (this.address.notes.length > 500) {
        array.push({ 
          field: 'notes', 
          rule: 'notes_maxlength', 
          message: 'Notes cannot be longer than 500 characters' 
        })
      } 

      if (this.productsAvailable && this.address.products.length < 1) {
        array.push({ 
          field: 'products', 
          rule: 'products_minlength', 
          message: 'You need to add at least one product item' 
        })
      }

      this.errors = array

      if (this.errors.length === 0) {
        let id = new Date().valueOf()
        this.address.formatted_address = `${this.address.address_1}, ${this.address.address_2 ? this.address.address_2 + ', ' : ''}${this.address.city}, ${this.address.zip}, ${this.address.country}`

        if (this.addressType && this.addressIndex > -1) {
          this.updateAddress({ 
            type: this.addressType, 
            index: this.addressIndex, 
            address: this.address 
          })
        } else {
          this.addAddress({ 
            id,
            type: this.addressType, 
            address: this.address 
          })
        }

        this.$emit('valid', { id, ...this.address })
        return true
      } else {
        this.$nextTick(() => {
          document.querySelector('.form-error').scrollIntoView({ 
            behavior: 'smooth', block: 'start', inline: 'nearest' 
          })
        })

        this.$emit('invalid')
        return false
      }
    }
  }
}
</script>